import React, { useContext, useState } from 'react';
import { Form, Card, Alert, Row, Col } from 'react-bootstrap';
import { ConsentFormState, DocumentSavingPreferenceType, DocumentSavingPreference } from '../../../types';
import { NavigationContext } from '../../../../../Context/NavigationContext';
import PdfDropzone from '../../../components/PdfDropzone';

interface ConsentFormStepProps {
    consentFormState: ConsentFormState;
    setConsentFormState: React.Dispatch<React.SetStateAction<ConsentFormState>>;
    setUploadedConsentFile: React.Dispatch<React.SetStateAction<File>>;
    documentSavingPreference: DocumentSavingPreferenceType;
}

const ConsentFormStep: React.FC<ConsentFormStepProps> = ({
    consentFormState,
    setConsentFormState,
    setUploadedConsentFile,
    documentSavingPreference
}) => {
    const nav = useContext(NavigationContext);
    
    // Determine if the default consent form option should be disabled
    const isDefaultConsentDisabled = documentSavingPreference !== DocumentSavingPreference.MYDRIVE;
    
    // Function to navigate to Options settings page
    const handleNavigateToOptions = () => {
        const OpenFirmProfileDocs = {
            component: "main",
            url: "lfmaster.asp",
            popup: false,
            params: {
                formasp: "EditUserProfile",
                goToSection: "other_SectionTable_Head,other_SectionTable",
            },
        };
        nav(OpenFirmProfileDocs);
    };
    
    // If default consent is disabled but currently selected, switch to upload option
    React.useEffect(() => {
        if (isDefaultConsentDisabled && consentFormState.defaultConsentFormRequired) {
            setConsentFormState({
                ...consentFormState,
                defaultConsentFormRequired: false,
                uploadConsentForm: true,
                consentFormRequired: true
            });
        }
    }, [isDefaultConsentDisabled, consentFormState.defaultConsentFormRequired]);
    
    const handleRadioChange = (type: 'default' | 'upload' | 'text') => {
        setConsentFormState({
            ...consentFormState,
            defaultConsentFormRequired: type === 'default',
            uploadConsentForm: type === 'upload',
            textBoxConsentFormRequired: type === 'text',
            consentFormRequired: true
        });
    };

    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    const handleFileSelect = (file: File) => {
        if (file.type !== 'application/pdf') {
            alert('Please upload a PDF file');
            return;
        }
        setUploadedConsentFile(file);
        setUploadedFileName(file.name);
    };

    const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setConsentFormState({
            ...consentFormState,
            textBasedConsentForm: event.target.value
        });
    };

    return (
        <Card>
            <Card.Header>
                <h4>Information Sharing Consent Form</h4>
            </Card.Header>
            <Card.Body>
                <Form>
                    <Row>
                        <Col md={12}>
                            <Form.Check
                                type="radio"
                                id="defaultConsent"
                                label="Use LDD Default Consent Form"
                                name="consentType"
                                checked={consentFormState.defaultConsentFormRequired}
                                onChange={() => handleRadioChange('default')}
                                disabled={isDefaultConsentDisabled}
                            />
                            {isDefaultConsentDisabled && (
                                <div className="mt-1 alert-area">
                                    <Alert variant="warning" role="alert" className="mb-0">
                                        <p className="text-muted mb-2" style={{ fontStyle: 'italic' }}>
                                            This option requires Document Saving Settings to be set to MyDrive. Current setting: <strong>{documentSavingPreference}</strong>
                                        </p>
                                        <button 
                                            type="button" 
                                            className="btn btn-link p-0 text-decoration-underline" 
                                            onClick={handleNavigateToOptions}
                                            style={{ fontSize: '0.875rem', fontStyle: 'italic' }}
                                        >
                                            Click here to change your document saving preference
                                        </button>
                                    </Alert>
                                </div>
                            )}
                            {consentFormState.defaultConsentFormRequired && (
                                <div className="mt-3">
                                    <Alert variant="info" className="mb-3">
                                    <p>
                                        The system will check your MyDrive for an existing consent form. 
                                        If none is found, a new form will be generated from your master template.
                                    </p>
                                    <p className="mb-0">
                                        <strong>Note:</strong> Generating a new consent form may take up to a few seconds.
                                    </p>
                                    </Alert>
                                </div>
                            )}
                        </Col>
                    </Row>
                    
                    <Row className="mt-3">
                        <Col md={12}>
                            <Form.Check
                                type="radio"
                                id="uploadConsent"
                                label="Upload PDF"
                                name="consentType"
                                checked={consentFormState.uploadConsentForm}
                                onChange={() => handleRadioChange('upload')}
                            />
                        </Col>
                    </Row>

                    {consentFormState.uploadConsentForm && (
                        <div className="mt-3">
                            <Form.Group className="">
                                <PdfDropzone 
                                    onFileSelect={handleFileSelect}
                                    uploadedFileName={uploadedFileName}
                                />
                            </Form.Group>
                        </div>
                    )}

                    <Row className="mt-3">
                        <Col md={12}>
                            <Form.Check
                                type="radio"
                                id="textConsent"
                                label="Enter Text"
                                name="consentType"
                                checked={consentFormState.textBoxConsentFormRequired}
                                onChange={() => handleRadioChange('text')}
                            />
                        </Col>
                    </Row>

                    {consentFormState.textBoxConsentFormRequired && (
                        <div className="mt-3">
                            <Form.Group>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    value={consentFormState.textBasedConsentForm || ''}
                                    onChange={handleTextChange}
                                    placeholder="Enter the consent form text here..."
                                />
                            </Form.Group>
                        </div>
                    )}
                </Form>
            </Card.Body>
        </Card>
    );
};

export default ConsentFormStep;
